import { Badge, Button, FormControl, InputBase, MenuItem, Paper, Select, Typography } from '@mui/material';
import clsx from 'clsx';
import { getChildZones, selectAllZones } from 'GeminiViewerComponent/features/asset/assetSlice';
import { uniqBy } from 'GeminiViewerComponent/helpers/lodashUtils';
import { useDebounce } from 'GeminiViewerComponent/hooks/useDebounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdArrowDropDown, MdArrowDropUp, MdSearch } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useStyles } from './styles';

const ZONES_ONLY = 1;
const ITEMS_ONLY = 2;
const ZONES_AND_ITEMS = ZONES_ONLY | ITEMS_ONLY;

const SearchFilter = ({ filterBadge, clearFilters, addFilters, children, searchItemSelected }) => {
    const loadedZones = useSelector(selectAllZones);
    const childZones = useSelector(getChildZones);
    const [toggleFilter, setToggleFilter] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [searchOption, setSearchOption] = useState(ZONES_AND_ITEMS);
    const [searchResults, setSearchResults] = useState(null);
    const searchResultsContainer = useRef(null);
    const classes = useStyles();

    const searchZonesAndItems = useMemo(() => {
        const allZones = uniqBy([...loadedZones, ...childZones], (z) => z.zone_id);

        return allZones.flatMap((z) => {
            return [
                {
                    zone_name: z.display_name,
                    zone_id: z.zone_id,
                },
                ...(z.items?.map((i) => ({
                    display_name: i.display_name,
                    external_id: i.external_id,
                    item_id: i.item_id,
                    zone_id: z.zone_id,
                    zone_name: z.display_name,
                })) ?? []),
            ];
        });
    }, [loadedZones, childZones]);

    useEffect(() => {
        if (searchString && loadedZones.length > 0) {
            const searchStr = searchString.toLowerCase();
            const filteredResults = searchZonesAndItems.filter(
                (z) =>
                    (searchOption & ZONES_ONLY &&
                        z.item_id === undefined &&
                        z.zone_name?.toLowerCase().includes(searchStr)) ||
                    (searchOption & ITEMS_ONLY &&
                        (z.display_name?.toLowerCase().includes(searchStr) ||
                            z.external_id?.toLowerCase().includes(searchStr)))
            );
            setSearchResults(filteredResults);
        }
    }, [loadedZones, searchString, searchOption, searchZonesAndItems]);

    const handleToggleFilter = () => {
        setToggleFilter(!toggleFilter);
    };

    const handleSearchResultClick = (item) => {
        searchItemSelected(item);
        setSearchResults(null);
        setSearchString('');
    };

    const search = (searchOption, searchString) => {
        if (!searchString) {
            setSearchResults(null);
            return;
        }

        searchString = searchString.toLowerCase();
        const filteredResults = searchZonesAndItems.filter(
            (z) =>
                (searchOption & ZONES_ONLY &&
                    z.item_id === undefined &&
                    z.zone_name?.toLowerCase().includes(searchString)) ||
                (searchOption & ITEMS_ONLY &&
                    (z.display_name?.toLowerCase().includes(searchString) ||
                        z.external_id?.toLowerCase().includes(searchString)))
        );

        setSearchResults(filteredResults);
        if (searchResultsContainer.current) searchResultsContainer.current.scrollTop = 0;
    };

    const debouncedSearch = useDebounce(search, 1000);

    const handleChange = (event) => {
        setSearchString(event.target.value);
        debouncedSearch(searchOption, event.target.value);
    };

    return (
        <div className={classes.searchFilterContainer}>
            <div className={classes.searchContainer}>
                <div className={classes.searchIcon}>
                    <MdSearch className="react-icon" />
                </div>
                <InputBase
                    placeholder="Search…"
                    id="viewer_title_search-input"
                    classes={{
                        root: classes.inputRoot,
                        input: classes.inputInput,
                    }}
                    onChange={handleChange}
                    value={searchString}
                    inputProps={{ type: 'search' }}
                />
                <div
                    id="viewer_title_search-filter-toggle"
                    className={classes.filterContent}
                    onClick={handleToggleFilter}
                >
                    <Badge
                        className={filterBadge ? classes.filterBadge : ''}
                        sx={{ ml: 'auto' }}
                        badgeContent={filterBadge}
                        invisible={!filterBadge}
                    >
                        <div className={classes.filterText}>FILTER</div>
                    </Badge>
                    {toggleFilter ? (
                        <MdArrowDropUp className="react-icon" />
                    ) : (
                        <MdArrowDropDown className="react-icon" />
                    )}
                </div>
            </div>
            <div className={classes.filterContainer}>
                {toggleFilter && (
                    <Paper id="simple-menu" onClick={() => handleToggleFilter}>
                        {children}
                        <MenuItem className={classes.filterButtons} disableRipple>
                            <div className={classes.filterClearButton}>
                                <Button
                                    id="viewer_title_search_clear-btn"
                                    className={classes.filterButtonPadding}
                                    onClick={() => {
                                        clearFilters();
                                        setToggleFilter(false);
                                    }}
                                >
                                    Clear
                                </Button>
                            </div>
                            <div>
                                <Button
                                    id="viewer_title_search_apply-filter-btn"
                                    className={classes.filterButtonPadding}
                                    onClick={() => {
                                        addFilters();
                                        setToggleFilter(false);
                                    }}
                                    color="secondary"
                                    variant="contained"
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </MenuItem>
                    </Paper>
                )}
            </div>
            {searchResults && (
                <Paper className={classes.searchResultsContainer} ref={searchResultsContainer}>
                    <div className={classes.searchResultsHeader}>
                        <h2>Search Results</h2>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={searchOption}
                                onChange={(e) => {
                                    setSearchOption(e.target.value);
                                    search(e.target.value, searchString);
                                }}
                            >
                                <MenuItem value={ZONES_AND_ITEMS}>Zones and Items</MenuItem>
                                <MenuItem value={ITEMS_ONLY}>Items Only</MenuItem>
                                <MenuItem value={ZONES_ONLY}>Zones Only</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className={classes.searchResultsItemsContainer}>
                        {searchResults.length === 0 && <Typography variant="body2">No results found.</Typography>}
                        {searchResults.map((item, idx) => {
                            return (
                                <div
                                    id={`viewer_title_search-item-${idx}`}
                                    data-item-id={item.item_id}
                                    key={`${item.zone_id}${item.item_id}`}
                                    className={clsx(classes.searchResultsItem, 'viewer_title_search-item')}
                                    onClick={() => handleSearchResultClick(item)}
                                >
                                    <span className={classes.searchResultsItemZone}>{item.zone_name}</span>
                                    {item.display_name && ` - ${item.display_name}`}
                                </div>
                            );
                        })}
                    </div>
                </Paper>
            )}
        </div>
    );
};

export default SearchFilter;
